import { Location } from "../constants/serve";
import { SERVER } from "./config"

const POST = "POST";
const GET = "GET";

export namespace Requestor {
  const PLATFORM = "partner-web";

  export async function request<T>(
    url: string,
    method: "GET" | "POST",
    headers?: Headers,
    body?: any
  ): Promise<{ data: T; headers: Headers }> {
    const req = new Request(url, {
      method,
      headers,
      body,
      mode: "cors",
    });

    const res = await fetch(req);

    if (res.status !== 200) {
      throw new Error(`StatusCode: ${res.status}`);
    }

    const hds = res.headers;
    const data: T = await res.json();

    return {
      data,
      headers: hds,
    };
  }

  export namespace Options {
    // 服务器返回结果
    export type Result<T> = {
      action: string;
      error: string;
      result: T;
      version: string;
    };

    // 登录参数
    export type LoginParam = {
      timestamp: number;
      payload: string;
    };
  }

  export class Factory {
    protected server = "";

    protected qrServer = "";

    protected headers = new Headers();

    constructor(server: string) {
      this.server = server;
      this.initHeaders();
    }

    private initHeaders() {
      this.headers.set("Content-Type", "application/json");
      this.headers.set("Platform", PLATFORM);

      // 初始化上次保存的token
      const t: string | null = sessionStorage.getItem("token");
      if (t === null) {
        sessionStorage.removeItem("user");
        return;
      }

      const token: { token: string; expire: number } = JSON.parse(t);

      if (token.expire < new Date().getTime() / 1000) {
        sessionStorage.removeItem("user");
        return;
      }

      this.headers.set("Authorization", token.token);
    }

    private checkXAuthToken(header: Headers) {
      const token = header.get("X-Auth-Token");
      if (token !== null && this.headers.get("Authorization") !== token) {
        sessionStorage.setItem(
          "token",
          JSON.stringify({
            token,
            expire: Math.ceil(new Date().getTime() / 1000) + 9 * 24 * 3600,
          })
        );
        this.headers.set("Authorization", token);
      }
    }

    protected async doRequest<T>(
      url: string,
      method: "GET" | "POST",
      body?: any
    ): Promise<Requestor.Options.Result<T>> {
      const { data, headers } = await request<Requestor.Options.Result<T>>(
        url,
        method,
        this.headers,
        body
      );

      if (data.error !== "") {
        console.debug(url, method, "返回错误信息", data.error);
      }

      this.checkXAuthToken(headers);
      return data;
    }

    getToken(): string | null {
      return this.headers.get("Authorization");
    }

    // 登录
    async login<T>(
      params: Requestor.Options.LoginParam
    ): Promise<Requestor.Options.Result<T>> {
      return await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/login`,
        POST,
        JSON.stringify(params)
      );
    }

    // 修改密碼
    async changePwd<T>(params: Record<string, string>) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/modifyPassword`,
        POST,
        JSON.stringify(params)
      );

      return res;
    }


    async Navigate<T>(lat1: string, lng1: string, lat2: string, lng2: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/common/agent/url`,
        POST,
        JSON.stringify({ lat1, lng1, lat2, lng2 })
      );

      return res;
    }


    async pageServices<T>(page: number, size: number, where = "", otherParams: string = '') {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/page/services?page=${page}&size=${size}&where=${where}${otherParams !== "" ? '&' : ''}${otherParams}`,
        GET
      );

      return res;
    }

    async getServiceDetails<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/service/details/${sid}`,
        GET
      );

      return res;
    }

    // 查询账单
    async getBill<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/bill/${sid}`,
        GET,
      );
      return res;
    }
    // 查询账单中的服务项目
    async getBillItems<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/billDetails/list/${sid}`,
        GET,
      );
      return res;
    }
    // 查询账单-支付详情
    async getBillPayment<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/payment/${sid}`,
        GET,
      );
      return res;
    }
    // 查询账单-挂账详情
    async getBillCredit<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/credit/${sid}`,
        GET,
      );
      return res;
    }

    async pagePartners<T>(page: number, size: number, where: string = "") {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/page/partners?page=${page}&size=${size}&where=${where}`,
        GET
      );

      return res;
    }
    // 查询导航路由信息
    async getNavigationRoutes<T>(sid: string, itemId: number, item: number) {
      return await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/service/navigationRoutes?sid=${sid}&itemId=${itemId}&item=${item}`,
        GET
      );
    }

    async draftLocations<T>(sid: string, locations: Location[]) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/draft/locations`,
        POST,
        JSON.stringify({ sid, locations })
      );

      return res;
    }

    // 查询服务时间节点
    async getServiceEvents<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/common/events/${sid} `,
        GET,
      );
      return res;
    }

    // 查询账单结算情况
    async financialStatistics<T>(start?: number, end?: number) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/financialStatistics?${start ? ('start=' + start + '&') : ""}${end ? ('end=' + end) : ''}`,
        GET,
      );
      return res;
    }

    // 核价list
    async getPriceList<T>(paramsStr: string){
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/pricing/page?${paramsStr}`,
        GET,
      )
      return res;
    } 

    // 核价详情
    async getPricingDetails<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/pricing/draft/${sid}`,
        GET,
      );
      return res;
    }

    // 查询核价服务项
    async getPriceItemsDetail<T>(sid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/pricing/items/${sid}`,
        GET,
      )
      return res;
    }

    // dashbord 总量统计
    async getCountTotal<T>() {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/countTotal`,
        GET,
      )
      return res;
    }

    // dashbord 结算趋势统计
    async getSttledStatistics<T>(startDate?: string, endDate?: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/settledStatistics`,
        GET,
      )
      return res;
    }

    // dashbord 服务数量统计
    async getServeStatistics<T>(startDate?: string, endDate?: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/serveStatistics`,
        GET,
      )
      return res;
    }

    // 获取查勘员
    async getPartnerAccount<T>({ page, size}: { page: number, size: number }) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/pageByPartnerId?page=${page}&size=${size}`,
        GET,
      )
      return res;
    }

    // 解绑
    async removeSurveyorRole<T>(openid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/removeSurveyorRole?openid=${openid}`,
        POST,
      )
      return res;
    }

    // 启用查勘员
    async enableSurveyor<T>(openid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/enableSurveyor?openid=${openid}`,
        GET,
      )
      return res;
    }

    // 禁用查勘员
    async disableSurveyor<T>(openid: string) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/disableSurveyor?openid=${openid}`,
        GET,
      )
      return res;
    }

    // 企业子公司查询
    async getpPrtnerSubsidiary<T>({ page, size}: { page: number, size: number}) {
      const res = await this.doRequest<T>(
        `${this.server}/v1/partnerAccount/page/partners/child?page=${page}&size=${size}`,
        GET,
      )
      return res;
    }
  }

  // 真正暴露出来的单例
  export const Instance = new Requestor.Factory(SERVER);
}

export default Requestor.Instance